import React, { useState, useEffect, useRef } from 'react';
import './styles.css';

const MODULE_NAME = '_114_compare_tag_search';
const printdebug = process.env.REACT_APP_DEBUG === 'true';
const debugprefix = `[${MODULE_NAME}]`;

// Search icon SVG
const SearchIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  >
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

// Tag icon SVG
const TagIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  >
    <path d="M12 2H2v10c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"></path>
    <path d="M19 2c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2h-8"></path>
    <line x1="6" y1="12" x2="6" y2="20"></line>
  </svg>
);

const GlobalTagSearch = ({ onTagSelect, limit = 3, onFilterChange, hidden = false, style = {} }) => {
  // Hide component if hidden prop is true
  if (hidden) return null;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceTimerRef = useRef(null);

  const handleSearch = async (query) => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      setError(null);
      if (onFilterChange) onFilterChange(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tags/search?q=${encodeURIComponent(query)}&limit=${limit}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error(`Search failed: ${response.statusText}`);

      const data = await response.json();
      // Only keep the top 3 results and strip "pattern:" prefix
      const processedTags = (data.tags || []).slice(0, 3).map(tag => ({
        ...tag,
        tag_name: tag.tag_name.replace(/^pattern:/i, '')
      }));

      setSearchResults(processedTags);
      
      if (onFilterChange) onFilterChange(query);

      if (printdebug) {
        console.log(`${debugprefix} Search results for "${query}":`, processedTags);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search tags';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (searchQuery.trim().length === 0) {
      setSearchResults([]);
      setError(null);
      if (onFilterChange) onFilterChange(null);
      return;
    }

    setLoading(true);
    debounceTimerRef.current = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchQuery]);

  const handleTagClick = (tag) => {
    if (onTagSelect) onTagSelect(tag);
    setSearchQuery(tag.tag_name);
    setSearchResults([]);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
    if (onFilterChange) onFilterChange(null);
  };

  return (
    <div
      className="global-tag-search-container"
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        zIndex: 1000,
        ...style
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <label style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap" }}>🏷️</label>
        <div style={{ position: "relative", flex: 1, maxWidth: 300 }}>
          <input
            type="text"
            placeholder="e.g., 'bias', 'pattern'..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 12px",
              background: "#222",
              color: "#fff",
              border: "1px solid #444",
              borderRadius: 4,
              fontSize: 14
            }}
          />
          
          {/* Results Dropdown - Absolute Positioning for Modal Behavior */}
          {((loading && searchQuery.length >= 2) || searchResults.length > 0 || error) && (
            <div 
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                background: "#1a1a1a",
                border: "1px solid #444",
                borderRadius: "0 0 4px 4px",
                boxShadow: "0 8px 16px rgba(0,0,0,0.6)",
                marginTop: 2,
                zIndex: 1001,
                maxHeight: "300px",
                overflowY: "auto"
              }}
            >
              {loading && <div style={{ padding: "8px 12px", fontSize: 12, color: "#888" }}>Searching...</div>}
              {error && <div style={{ padding: "8px 12px", color: "#e63946", fontSize: 12 }}>{error}</div>}
              {searchResults.map((tag) => (
                <div
                  key={tag.id}
                  onClick={() => handleTagClick(tag)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    borderBottom: "1px solid #333",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#2a2a2a"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <TagIcon size={14} />
                    <span style={{ fontSize: 13, color: "#00ffff" }}>{tag.tag_name}</span>
                  </div>
                  <span style={{ fontSize: 11, color: "#666" }}>{tag.usage_count} uses</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {searchQuery && (
          <button
            onClick={clearSearch}
            style={{
              padding: "6px 12px",
              background: "#e63946",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: "nowrap"
            }}
            type="button"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export default GlobalTagSearch;